import { useEffect, useRef, useState } from "react";
import { saveEvent } from "../../../api/userevents";
import xapiverbs from "../../../config/xapiverbs";
import { useCourse } from "../../../contexts/CourseProvider";
import { answerOnboardingQuestion } from "../../../api/postactivity";

export default function OnboardingConsent({ activityid, text_html, onChange, onComplete }) {
  const [consentGiven, setConsentGiven] = useState(null);
  const [highlight, setHighlight] = useState(false);
  const hasHighlightedRef = useRef(false);
  const containerRef = useRef(null);
  const { course } = useCourse();

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasHighlightedRef.current) {
          hasHighlightedRef.current = true;
          setHighlight(true);
          setTimeout(() => setHighlight(false), 900);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const saveAnswer = async () => {
    await saveEvent(activityid, course.courseid, xapiverbs.ANSWERED, {
      component: "Consent",
      consent: consentGiven,
    });
    await answerOnboardingQuestion(activityid, { consentGiven });
  };

  useEffect(() => {
    if (consentGiven !== null) {
      onComplete?.(consentGiven != null);
      saveAnswer();
    }
  }, [consentGiven, onComplete]);

  const handleChange = (value) => {
    setConsentGiven(value);
    onChange?.(value);
  };

  return (
    <>
      <div className="text-md mb-1 text-left">
        <div dangerouslySetInnerHTML={{ __html: text_html }} />
      </div>

      <div ref={containerRef} className="mx-auto">
        <fieldset className="p-2 rounded">
          <legend className="sr-only">Research consent</legend>

          {/* Consent: label wraps input + text (text is clickable) */}
          <label className="flex items-center mb-3 cursor-pointer">
            <input
              type="radio"
              name="consent"
              value="yes"
              checked={consentGiven === true}
              onChange={() => handleChange(true)}
              className={
                highlight
                  ? "mr-2 accent-blue-600 ring-4 ring-yellow-300 rounded-full transition-shadow duration-500"
                  : "mr-2 accent-blue-600 transition-shadow duration-500"
              }
            />
            <span>I consent to participate in the study</span>
          </label>

          {/* Do NOT consent: only the radio is clickable */}
          <div className="flex items-center">
            <input
              id="consent-no"
              type="radio"
              name="consent"
              value="no"
              checked={consentGiven === false}
              onChange={() => handleChange(false)}
              className="mr-2 accent-blue-600 cursor-pointer"
            />
            <span className="cursor-default select-none">I do not consent to participate in the study</span>
          </div>
        </fieldset>
      </div>
    </>
  );
}
